import React, { useEffect, useState } from "react";
import TPC_URL from "../../../utils/TPC_URL";
import axios from "axios";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Alert,
  Button,
  CircularProgress,
  Snackbar,
  Grid,
  Box,
  Tooltip,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { motion } from "framer-motion";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import EditIcon from "@mui/icons-material/Edit";
import SpeedIcon from "@mui/icons-material/Speed";
import InventoryIcon from "@mui/icons-material/Inventory";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import EventNoteIcon from "@mui/icons-material/EventNote";
import goToHelpLink from "../../../utils/goToHelpLink";
import * as S from "./styles";
import ContentCutIcon from "@mui/icons-material/ContentCut";

function CustomSettings() {
  const { group_code, project_code } = useParams();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [originalSettings, setOriginalSettings] = useState([]);
  const [mounted, setMounted] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const settingsInfo = {
    pce: {
      title: "PCE (Process Cycle Efficiency)",
      description:
        "생산 공정의 효율성을 나타내는 값입니다. 값이 클수록 높은 효율을 의미합니다.",
      icon: <SpeedIcon sx={{ fontSize: 28 }} />,
      color: "#4caf50",
      unit: "%",
      min: 0,
      max: 100,
      default: 50,
    },
    lot_size: {
      title: "LOT 크기",
      description: "생산 단위당 처리되는 수량을 나타냅니다.",
      icon: <InventoryIcon sx={{ fontSize: 28 }} />,
      color: "#2196f3",
      unit: "EA",
      min: 1,
      max: 1000,
      default: 100,
    },
    end_margin_hours: {
      title: "종료 여유 시간",
      description: "계획된 작업 종료 후 추가적으로 확보할 여유 시간입니다.",
      icon: <AccessTimeIcon sx={{ fontSize: 28 }} />,
      color: "#ff9800",
      unit: "초",
      min: 0,
      max: 3600,
      default: 1800,
    },
    alignment_option: {
      title: "정렬 옵션",
      description:
        "계획 시작(Start) 또는 종료(End) 기준으로 정렬하는 옵션입니다.",
      icon: <CompareArrowsIcon sx={{ fontSize: 28 }} />,
      color: "#9c27b0",
      options: ["End", "Start"],
      default: 0, // 0 = End, 1 = Start
    },
    cutting_process: {
      title: "절단공정 시행일",
      description: "절단공정을 실행할 날짜를 설정합니다.",
      icon: <EventNoteIcon sx={{ fontSize: 28 }} />,
      color: "#f44336",
      unit: "일 전",
      min: 0,
      max: 365,
      default: 0,
    },
    cutting_bundle_day: {
      title: "절단 공정 묶음 일수",
      description: "해당된 날짜만큼 절단 공정을 묶어서 일정계획을 수립합니다.",
      icon: <ContentCutIcon sx={{ fontSize: 28 }} />,
      color: "#00bcd4",
      unit: "일",
      min: 1,
      max: 30,
      default: 3,
    },
        base_time: {
      title: "기준 시간",
      description:
        "기준 시간이 활성화되면 해당 시간부터 계획을 시작합니다. 비활성화 시 서버의 현재 시간을 기준으로 합니다.",
      icon: <AccessTimeIcon sx={{ fontSize: 28 }} />,
      color: "#607d8b",
      default: {
        status: 0,
        time_property: null,
      },
    },
  };
  
  // 기본 설정 생성 함수
  const createDefaultSettings = () => {
    return Object.keys(settingsInfo).map((key) => ({
      property_name: key,
      property_value: settingsInfo[key].default,
      update_time: new Date().toISOString(),
      isNew: true, // 새로 생성된 설정임을 표시
    }));
  };

  // 설정 병합 함수 (API 데이터와 기본값 병합)
  const mergeWithDefaults = (apiSettings) => {
    const defaultSettings = createDefaultSettings();
    const apiMap = new Map(
      apiSettings.map((s) => [s.property_name, s])
    );

    return defaultSettings.map((defaultSetting) => {
      const apiSetting = apiMap.get(defaultSetting.property_name);
      if (apiSetting) {
        // API에 데이터가 있으면 사용 (isNew 제거)
        return { ...apiSetting, isNew: false };
      }
      // API에 데이터가 없으면 기본값 사용
      return defaultSetting;
    });
  };

  useEffect(() => {
    fetchSettings();
    setMounted(true);
    return () => {
      setMounted(false);
    };
  }, [group_code, project_code]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        TPC_URL +
          `/api/tpc/getCustomSettings?group_code=${group_code}&project_code=${project_code}`
      );

      let apiSettings = [];
      if (res.data && Array.isArray(res.data)) {
        apiSettings = res.data.map((item) => {
          let parsedValue = item.property_value;
          if (typeof parsedValue === "string") {
            try {
              parsedValue = JSON.parse(parsedValue);
            } catch (e) {
              // 파싱 실패 시 원본 유지
            }
          }
          return {
            ...item,
            property_value: parsedValue,
          };
        });
      }

      // API 데이터와 기본값 병합
      const mergedSettings = mergeWithDefaults(apiSettings);

      console.log("=== 병합된 설정 데이터 ===");
      mergedSettings.forEach((s) => {
        console.log(
          `${s.property_name}:`,
          s.property_value,
          s.isNew ? "(기본값)" : "(API)"
        );
      });

      setSettings(mergedSettings);
      setOriginalSettings(JSON.parse(JSON.stringify(mergedSettings)));
    } catch (error) {
      console.error("Error fetching settings:", error);
      openSnackbar("설정을 불러오는 중 오류가 발생했습니다.", "error");
      
      // API 실패 시에도 기본값으로 표시
      const defaultSettings = createDefaultSettings();
      setSettings(defaultSettings);
      setOriginalSettings(JSON.parse(JSON.stringify(defaultSettings)));
    } finally {
      setLoading(false);
    }
  };

  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleToggleEditMode = () => {
    if (editMode) {
      setSettings(JSON.parse(JSON.stringify(originalSettings)));
    } else {
      setOriginalSettings(JSON.parse(JSON.stringify(settings)));
    }
    setEditMode(!editMode);
  };

  const handleValueChange = (name, value) => {
    setSettings((prevSettings) =>
      prevSettings.map((setting) =>
        setting.property_name === name
          ? { ...setting, property_value: value }
          : setting
      )
    );
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      // 모든 설정을 저장 (기본값이든 수정된 값이든)
      const settingsToSave = settings.map((setting) => ({
        property_name: setting.property_name,
        property_value: setting.property_value,
      }));

      console.log("=== 저장할 데이터 ===", settingsToSave);

      const res = await axios.post(TPC_URL + "/api/tpc/updateCustomSettings", {
        group_code,
        project_code,
        settings: settingsToSave,
      });

      if (res.status === 200) {
        openSnackbar("설정이 성공적으로 저장되었습니다.", "success");
        await fetchSettings(); // 저장 후 다시 불러오기
        setEditMode(false);
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      openSnackbar("설정 저장 중 오류가 발생했습니다.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(JSON.parse(JSON.stringify(originalSettings)));
    setEditMode(false);
    openSnackbar("변경사항이 취소되었습니다.", "info");
  };

  const isSettingsChanged = () => {
    if (!originalSettings.length || !settings.length) return false;
    return settings.some((setting, index) => {
      const original = originalSettings.find(
        (s) => s.property_name === setting.property_name
      );
      if (!original) return true;
      return (
        JSON.stringify(setting.property_value) !==
        JSON.stringify(original.property_value)
      );
    });
  };

  const renderSettingCard = (setting) => {
    const info = settingsInfo[setting.property_name];

    if (!info) return null;

    const isBaseTimeSetting = setting.property_name === "base_time";

    // 디버깅 로그
    if (isBaseTimeSetting) {
      console.log("=== renderSettingCard (base_time) ===");
      console.log("property_value:", setting.property_value);
      console.log("status:", setting.property_value?.status);
    }

    return (
      <S.StyledCard>
        <S.CardHeader>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                bgcolor: info.color,
                color: "white",
                p: 1,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {info.icon}
            </Box>
            <S.Title variant="h6">{info.title}</S.Title>
          </Box>
          {setting.isNew && (
            <Box
              sx={{
                bgcolor: "#ff9800",
                color: "white",
                px: 1.5,
                py: 0.5,
                borderRadius: "12px",
                fontSize: "0.75rem",
                fontWeight: "bold",
              }}
            >
              기본값
            </Box>
          )}
        </S.CardHeader>
        <Box sx={{ p: 3 }}>
          <S.InfoItem>
            <S.IconWrapper>{info.icon}</S.IconWrapper>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <S.InfoText>현재 값: </S.InfoText>
              {setting.property_name === "alignment_option" ? (
                editMode ? (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Number(setting.property_value) === 1}
                        onChange={(e) =>
                          handleValueChange(
                            setting.property_name,
                            e.target.checked ? 1 : 0
                          )
                        }
                        color="primary"
                      />
                    }
                    label={
                      Number(setting.property_value) === 1 ? "Start" : "End"
                    }
                  />
                ) : (
                  <S.ValueText>
                    {Number(setting.property_value) === 1 ? "Start" : "End"}
                  </S.ValueText>
                )
              ) : isBaseTimeSetting ? (
                editMode ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Number(setting.property_value?.status) === 1}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) {
                              const currentTime = dayjs().format(
                                "YYYY-MM-DD HH:mm:ss"
                              );
                              handleValueChange(setting.property_name, {
                                status: 1,
                                time_property:
                                  setting.property_value?.time_property ||
                                  currentTime,
                              });
                            } else {
                              handleValueChange(setting.property_name, {
                                status: 0,
                                time_property: null,
                              });
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={
                        Number(setting.property_value?.status) === 1
                          ? "사용자 설정 기준 시간 사용"
                          : "현재 시간 사용"
                      }
                    />
                    <TextField
                      fullWidth
                      type="datetime-local"
                      size="small"
                      margin="normal"
                      disabled={Number(setting.property_value?.status) === 0}
                      value={
                        setting.property_value?.time_property &&
                        dayjs(setting.property_value.time_property).isValid()
                          ? dayjs(setting.property_value.time_property).format(
                              "YYYY-MM-DDTHH:mm"
                            )
                          : ""
                      }
                      onChange={(e) => {
                        const newValue = e.target.value;
                        const formatted = newValue
                          ? dayjs(newValue).format("YYYY-MM-DD HH:mm:ss")
                          : "";
                        handleValueChange(setting.property_name, {
                          status: 1,
                          time_property: formatted,
                        });
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <S.ValueText>
                      {Number(setting.property_value?.status) === 1
                        ? "사용자 설정 기준 시간 사용"
                        : "현재 시간 사용"}
                    </S.ValueText>
                    {Number(setting.property_value?.status) === 1 &&
                      setting.property_value?.time_property && (
                        <S.ValueText
                          sx={{ fontSize: "0.9rem", color: "text.secondary" }}
                        >
                          설정 시간: {setting.property_value.time_property}
                        </S.ValueText>
                      )}
                  </Box>
                )
              ) : editMode ? (
                <TextField
                  fullWidth
                  type={info.isString ? "text" : "number"}
                  value={setting.property_value}
                  onChange={(e) =>
                    handleValueChange(
                      setting.property_name,
                      info.isString
                        ? e.target.value
                        : parseInt(e.target.value) || 0
                    )
                  }
                  variant="outlined"
                  size="small"
                  margin="normal"
                  InputProps={{
                    endAdornment: info.unit ? (
                      <InputAdornment position="end">
                        {info.unit}
                      </InputAdornment>
                    ) : null,
                  }}
                  inputProps={
                    !info.isString
                      ? {
                          min: info.min,
                          max: info.max,
                        }
                      : undefined
                  }
                />
              ) : (
                <S.ValueText>
                  {setting.property_value}
                  {info.unit && !info.isString && ` ${info.unit}`}
                </S.ValueText>
              )}
            </Box>
          </S.InfoItem>
          <Box sx={{ mt: 2 }}>
            <S.InfoText>설명: </S.InfoText>
            <S.InfoText>{info.description}</S.InfoText>
          </Box>
          {!setting.isNew && (
            <Box sx={{ mt: 3, fontSize: "0.75rem", color: "text.secondary" }}>
              최종 업데이트: {new Date(setting.update_time).toLocaleString()}
            </Box>
          )}
        </Box>
      </S.StyledCard>
    );
  };

  return (
    <S.PageContainer>
      <S.HeaderContainer>
        <S.Title
          variant="h5"
          fontWeight="bold"
          sx={{
            display: "flex",
            alignItems: "center",
            fontSize: "22px !important",
          }}
        >
          AI 고급 설정 &nbsp;
          <Tooltip
            title="도움말"
            sx={{ cursor: "pointer" }}
            onClick={() => goToHelpLink("https://help.ese-tech.com/ko/planner")}
          >
            <HelpOutlineIcon />
          </Tooltip>
        </S.Title>
        <S.TopBtnBox>
          {editMode ? (
            <>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                disabled={!isSettingsChanged() || loading || saving}
              >
                취소
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                disabled={!isSettingsChanged() || loading || saving}
              >
                {saving ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "저장"
                )}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleToggleEditMode}
              disabled={loading}
            >
              설정 수정
            </Button>
          )}
        </S.TopBtnBox>
      </S.HeaderContainer>

      <S.ContentContainer>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ height: "calc(100% - 24px)" }}>
            {settings.map((setting, index) => (
              <Grid
                item
                xs={12}
                sm={4}
                key={setting.property_name}
                sx={{
                  height: "50%",
                  display: "flex",
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: mounted ? 1 : 0, y: mounted ? 0 : 20 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  style={{ height: "100%", width: "100%" }}
                >
                  {renderSettingCard(setting)}
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}
      </S.ContentContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </S.PageContainer>
  );
}

export default CustomSettings;
